import React from "react";
import {
  Sparkles,
  BookOpen,
  Target,
  Clock,
  ArrowLeft,
  Play,
} from "lucide-react";
import "./IntroScreen.scss";

/**
 * Parses a Java Duration-style ISO-8601 string (e.g. "PT10M", "PT1H30M") into
 * whole minutes. Returns null for anything missing/unparseable/zero, so
 * callers can fall back to "no time limit" rather than showing "~0 min".
 */
const parseDurationMinutes = (iso) => {
  if (!iso) return null;
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso);
  if (!match) return null;
  const [, h, m, s] = match;
  const totalMinutes =
    (Number(h) || 0) * 60 + (Number(m) || 0) + (Number(s) || 0) / 60;
  return totalMinutes > 0 ? Math.round(totalMinutes) : null;
};

/**
 * IntroScreen
 * Pre-test landing: title, description, quick stats, section preview,
 * and the primary "Start" call to action.
 */
const IntroScreen = ({ testData, onBack, onStart, starting }) => {
  const totalQuestions =
    testData.sections?.reduce(
      (sum, s) => sum + (s.questions?.length || 0),
      0,
    ) || 0;

  const canAttempt = testData.canAttempt !== false;
  const durationMinutes = parseDurationMinutes(testData.testduration);

  return (
    <div className="intro-screen">
      <div className="intro-inner">
        <span className="intro-eyebrow">
          <Sparkles /> Assessment
        </span>

        <h1>{testData.testTitle || testData.title || "Assessment"}</h1>
        <p className="intro-description">
          {testData.description ||
            "A short set of questions designed to give you clear, useful insight."}
        </p>

        <div className="intro-stats">
          <div className="stat-card">
            <BookOpen />
            <strong>{testData.sections?.length || 1} sections</strong>
            <span>Grouped by theme</span>
          </div>
          <div className="stat-card">
            <Target />
            <strong>{totalQuestions} questions</strong>
            <span>One at a time, no clutter</span>
          </div>
          <div className="stat-card">
            <Clock />
            <strong>
              {durationMinutes != null
                ? `${durationMinutes} min`
                : "No time limit"}
            </strong>
            <span>
              {durationMinutes != null
                ? "Time-boxed attempt"
                : "Take as long as you need"}
            </span>
          </div>
        </div>

        <div className="intro-sections">
          <h3>What you'll cover</h3>
          {testData.sections?.map((section, index) => (
            <div className="section-row" key={index}>
              <span className="section-index">{index + 1}</span>
              <div>
                <h4>{section.sectionTitle}</h4>
                <p>{section.questions?.length || 0} questions</p>
              </div>
            </div>
          ))}
        </div>

        <div className="intro-actions">
          <button type="button" className="back-button" onClick={onBack}>
            <ArrowLeft size={16} /> Back to dashboard
          </button>
          <span
            className="start-button-wrap"
            data-tooltip="You cannot take the test again"
            data-disabled={!canAttempt}
          >
            <button
              type="button"
              className={`start-button ${!canAttempt ? "start-button--disabled" : ""}`}
              onClick={onStart}
              disabled={!canAttempt || starting}
              aria-disabled={!canAttempt || starting}
            >
              <Play /> Start assessment
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

export default IntroScreen;
